// src/oura/components/DataConnectors.tsx
//
// The Connectors tab: card-grid connectors grouped by category, with brand
// marks. Rendered as open sections (no outer card chrome). GSC/GA4 power the
// traffic and indexed-page numbers; the website platform is how ourteam
// publishes approved fixes. Client documents (Google Docs) is status only:
// Oura reads the client's Drive folder as oura@ourteam.ai, so it has no
// Connect or Disconnect.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
    AlertTriangleIcon,
    CheckCircle2Icon,
    ChevronDownIcon,
    ExternalLinkIcon,
    FolderIcon,
    Link2Icon,
    Loader2Icon,
    RefreshCwIcon,
    XIcon,
} from "lucide-react";
import { cn } from "../lib/cn";
import type { DataConnector, ConnectorCategory, DocsConnectorView } from "../connectors";
import {
    CATEGORY_META,
    GOOGLE_DOCS_CONNECTOR,
    STATUS_ONLY_CONNECTORS,
    docsConnectorView,
    publishableTypeLabel,
} from "../connectors";
import type { GscState } from "../gscState";
import { ouraHref } from "../nav";
import { Button } from "../primitives/Button";
import { Dot, PillLink } from "@oura/ui";
import { agoText, useDocsConnection, useDocsFolder } from "@oura/docs";
import { DOCS_WRITE_ON } from "@oura/docs/writeFlag";
import type { DocsWriteStatus } from "@service/aeo/clientDocsWrite";

import { ConnectorLogo } from "./connectorLogos";
import { ConnectorConnectModal, CREDENTIAL_CONNECTORS } from "./ConnectorConnectModal";
import { connectConnector, disconnectConnector, syncConnector } from "@service/aeo";
import { useSnackbar } from "@hook/use-snackbar";

const CATEGORY_ORDER: ConnectorCategory[] = ["search", "cms", "docs"];

/**
 * The Client tab with its Client folder card open: `?folder=1`. The Client
 * tab reads it once, opens the card (and puts focus in the link field when
 * no folder is linked yet), then drops it, the way `?compose=note` works.
 */
function clientFolderHref(businessId: string): string {
    return `${ouraHref("reviews", { role: "oura-staff", businessId })}?folder=1`;
}

/**
 * Google Business Profile is not an Oura connector: the backend catalogue
 * dropped it (#3801), so a card for it could only be stale data offering a
 * setup nothing finishes. It is left out before anything is drawn or counted.
 */
function isOuraConnector(c: DataConnector): boolean {
    return c.key !== "gbp" && c.category !== "profile";
}

/**
 * Categories whose connectors are ALTERNATIVES, not additions.
 *
 * A site is built on one platform. WordPress and Framer sat side by side as
 * peer cards, each with its own primary "Connect", which reads as a checklist
 * to work through — and no one can work through it, because connecting both is
 * not a thing a site can be. Only the presentation changes here: every card
 * keeps the same actions, the alternatives are just folded away once a platform
 * is settled instead of shouting for a click that would never make sense.
 */
const EXCLUSIVE_CATEGORIES: ReadonlySet<ConnectorCategory> = new Set<ConnectorCategory>(["cms"]);

/** Said once per exclusive section, so the choice is stated and not inferred. */
const EXCLUSIVE_HINT = "A site publishes through one platform. Connect just the one it is built on.";

/** The keys `POST /connectors/{key}/sync` accepts (ourteam-backend, Aug fixes).
 *  Other connectors have no on-demand pull, so their cards carry no Refresh. */
const SYNCABLE_CONNECTORS = new Set(["gsc", "ga4", "wordpress", "framer"]);

/**
 * Which consent hosts a connector is allowed to send the browser to.
 *
 * `connectConnector()` only ever returns a `redirect_url` for the Google
 * connectors (ourteam-backend#3685) — the credential-based ones store a
 * connection and come back `kind: "connected"`. So the map covers exactly those
 * two (Business Profile left the catalogue, #3801), and a redirect claimed for
 * any other key is refused rather than followed: navigating on a
 * backend-supplied string is a trust boundary, and an unlisted host means
 * either the contract changed or the value is not ours.
 */
const OAUTH_HOST_ALLOWLIST: Record<string, RegExp[]> = {
    gsc: [/^accounts\.google\.com$/i, /(^|\.)googleapis\.com$/i],
    ga4: [/^accounts\.google\.com$/i, /(^|\.)googleapis\.com$/i],
};

/**
 * The consent URL to navigate to, or `null` if it is not one we trust.
 *
 * `https:` only — that alone rejects `javascript:` and `data:`, which a
 * `location.href =` would otherwise execute in our own origin.
 */
function getSafeConsentUrl(connectorKey: string, rawUrl: string): string | null {
    try {
        const url = new URL(rawUrl);
        if (url.protocol !== "https:") return null;
        const allowedHosts = OAUTH_HOST_ALLOWLIST[connectorKey] ?? [];
        return allowedHosts.some((re) => re.test(url.hostname)) ? url.toString() : null;
    } catch {
        return null;
    }
}

function ConnectorCard({
    c,
    busy,
    refreshing,
    health,
    syncNote,
    onConnect,
    onDisconnect,
    onRefresh,
}: {
    c: DataConnector;
    busy: boolean;
    /** The in-flight action is specifically a refresh, so the Refresh button
     *  owns the working state instead of Disconnect claiming it. */
    refreshing: boolean;
    /**
     * What the DATA says about this connector, where we can check it.
     *
     * A grant is not a working connection. Where a second source exists — for
     * Search Console, the scan's own search block — the card states what
     * actually arrived instead of restating the grant in green. Undefined for
     * connectors with nothing to reconcile against, which keeps this card the
     * same as it always was for them.
     */
    health?: GscState;
    /** What the last on-demand refresh came back with when it did NOT come
     *  back connected — the BE's own `detail`, rendered on the card because a
     *  failed pull is an answer about this connector, not an app error. */
    syncNote?: string;
    onConnect: () => void;
    onDisconnect: () => void;
    /** Re-pull this connector now. Absent when the connector has no sync
     *  endpoint or the business is a fixture. */
    onRefresh?: () => void;
}) {
    const connected = c.status === "connected";
    const degraded = connected && health?.tone === "warn";
    return (
        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-gray-300">
            <div className="flex items-start gap-3">
                <ConnectorLogo k={c.key} />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">{c.label}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{c.blurb}</p>
                    {c.status !== "not_connected" && (
                        <p className="mt-1 truncate text-xs text-gray-400">{c.detail}</p>
                    )}
                    {/* What this platform can actually write (#3692). Shown on
                        the card because the answer belongs beside the thing it
                        describes — a Wix site publishes two of our four fix
                        types, and finding that out at the Publish button is
                        finding out too late.

                        Only ever rendered from a list the backend sent: absent
                        means it never said, and an empty list means it said
                        "none", which is worth printing rather than hiding. */}
                    {connected && c.publishableTypes && (
                        <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
                            {c.publishableTypes.length > 0
                                ? `Publishes ${c.publishableTypes.map(publishableTypeLabel).join(", ")}.`
                                : "Oura cannot publish to this platform yet. Fixes come with steps to apply by hand."}
                        </p>
                    )}
                    {/* The cause, in full, under the account line. The status
                        line above has to fit a chip; this is where an operator
                        finds out what to do about it. */}
                    {degraded && health?.detail && (
                        <p className="mt-1.5 text-xs leading-relaxed text-amber-700">{health.detail}</p>
                    )}
                    {/* The last refresh ran and failed. The BE says why in
                        plain words; the card is where that answer belongs. */}
                    {syncNote && (
                        <p className="mt-1.5 text-xs leading-relaxed text-amber-700">{syncNote}</p>
                    )}
                </div>
            </div>
            <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                {connected ? (
                    <>
                        {degraded ? (
                            <span className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-amber-600">
                                <AlertTriangleIcon className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{health?.label ?? "Connected, no data"}</span>
                            </span>
                        ) : (
                            <span className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-emerald-600">
                                <CheckCircle2Icon className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{health?.label ?? "Connected"}</span>
                            </span>
                        )}
                        <span className="flex flex-shrink-0 items-center gap-3">
                            {/* Re-pull without the paid full re-scan — before
                                this the only lever was "Rerun assessment". */}
                            {onRefresh && (
                                <button
                                    onClick={onRefresh}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 disabled:opacity-50"
                                >
                                    <RefreshCwIcon className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
                                    {refreshing ? "Refreshing…" : "Refresh"}
                                </button>
                            )}
                            <button
                                onClick={onDisconnect}
                                disabled={busy}
                                className="text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50"
                            >
                                {busy && !refreshing ? "Working…" : "Disconnect"}
                            </button>
                        </span>
                    </>
                ) : (
                    <Button
                        size="sm"
                        variant={c.status === "action_needed" ? "secondary" : "default"}
                        className="w-full"
                        disabled={busy}
                        onClick={onConnect}
                    >
                        {busy ? (
                            <>
                                <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                Connecting…
                            </>
                        ) : c.status === "action_needed" ? (
                            "Finish setup"
                        ) : (
                            "Connect"
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * One category of connectors, with its own heading and status.
 *
 * Split out of DataConnectors so the "other platforms" fold can hold state:
 * inlining it in the map would put a hook inside a loop.
 */
function CategorySection({
    cat,
    items,
    renderCard,
    summary,
}: {
    cat: ConnectorCategory;
    items: DataConnector[];
    /** Rendering a card is the parent's job — every handler, and the busy and
     *  health state behind them, lives up there. This section only decides
     *  which cards are on show. */
    renderCard: (c: DataConnector) => ReactNode;
    /** In place of the section's "n/total connected". `null` shows nothing:
     *  a status-only section says its state on its card, not from the
     *  catalog's `status`. */
    summary?: ReactNode;
}) {
    const meta = CATEGORY_META[cat];
    const exclusive = EXCLUSIVE_CATEGORIES.has(cat);
    const connected = items.filter((c) => c.status === "connected");
    const [showAlternates, setShowAlternates] = useState(false);

    /**
     * Once a platform is connected the rest are alternatives to it, so they
     * fold away. Nothing settled yet — including a half-finished
     * `action_needed` — means this is still the moment of choosing, and every
     * option stays on show.
     *
     * Filtered on the CONNECTED ones rather than the first match: if a backend
     * ever reports two platforms at once, both stay visible. Hiding a live
     * connection behind a fold would be a worse lie than the one this fixes.
     */
    const folding = exclusive && connected.length > 0;
    const shown = folding ? connected : items;
    const alternates = folding ? items.filter((c) => c.status !== "connected") : [];

    return (
        <div>
            <div className="mb-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="text-xs font-semibold text-gray-500">{meta.label}</h4>
                    <p className="mt-0.5 text-xs text-gray-400">
                        {meta.description}
                        {exclusive && <> {EXCLUSIVE_HINT}</>}
                    </p>
                </div>
                {/* Per-section, because one number over the whole tab cannot say
                    anything true about a section where the cards are a choice. */}
                {summary === undefined ? (
                    <span className="mt-0.5 whitespace-nowrap text-xs text-gray-400 tabular-nums">
                        {exclusive
                            ? connected.length > 0
                                ? `${connected.map((c) => c.label).join(" · ")} connected`
                                : "None connected"
                            : `${connected.length}/${items.length} connected`}
                    </span>
                ) : (
                    summary !== null && (
                        <span className="mt-0.5 whitespace-nowrap text-xs text-gray-400 tabular-nums">{summary}</span>
                    )
                )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((c) => renderCard(c))}
            </div>

            {/* The escape hatch for the case the fold gets wrong: the site is
                not on the platform we have connected. Same cards, same
                buttons — one click further away, and only while the answer
                looks settled. */}
            {alternates.length > 0 && (
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={() => setShowAlternates((v) => !v)}
                        aria-expanded={showAlternates}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 transition-colors hover:text-blue-800"
                    >
                        {showAlternates
                            ? "Hide other platforms"
                            : `Site not on ${connected[0].label}? Show ${alternates.length} other ${alternates.length === 1 ? "platform" : "platforms"}`}
                        <ChevronDownIcon
                            className={cn("h-3 w-3 transition-transform", showAlternates && "rotate-180")}
                        />
                    </button>
                    {showAlternates && (
                        <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                            {alternates.map((c) => renderCard(c))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** "Writes client docs as oura@ourteam.ai. Write access checked 4 min ago.", or what is still missing. */
function docsWriteText(w: DocsWriteStatus | null | undefined, now: number = Date.now()): string {
    if (!w || !w.actingAs) return "Read only. Writing the docs isn't set up yet.";
    if (w.drive !== "write" || w.docs !== "write") return `Read only. ${w.actingAs} needs Drive and Docs write.`;
    const ago = agoText(w.lastWriteCheckAt, now);
    return `Writes client docs as ${w.actingAs}. ${ago ? `Write access checked ${ago}.` : "Write access not checked yet."}`;
}

/**
 * The Google Docs card's status: a dot and the word ("Connected" emerald),
 * said once. The word is always there, so colour is never the only signal.
 */
function DocsStatus({ tag }: { tag: NonNullable<DocsConnectorView["tag"]> }) {
    return (
        <span className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-gray-700">
            <Dot tone={tag.tone} />
            <span className="truncate">{tag.label}</span>
        </span>
    );
}

/**
 * The Google Docs card. Status only: no Connect, no Disconnect. Built on the
 * same shell as ConnectorCard so it reads as one of its siblings: what Oura
 * reads and as whom and when it last read up top, the status word at the
 * bottom left, and at most one in-app link at the bottom right (link the
 * folder, or open the Client tab's folder card). When oura@ourteam.ai has no
 * access, a second link opens the folder in Drive, where its sharing is fixed.
 */
function DocsConnectorCard({
    label,
    view,
    folderHref,
    writeLine,
}: {
    label: string;
    view: DocsConnectorView;
    /** Null when no client is on screen: there is no folder to go to. */
    folderHref: string | null;
    /** Phase 2 (B11): who Oura writes the docs as, and when that was checked. Null without the write switch. */
    writeLine: string | null;
}) {
    const loading = view.state === "loading";
    // The card folds by its head; folded, it keeps its title and one fact (the status).
    const [folded, setFolded] = useState(false);
    // Two links share the footer only when there is no access; small pills keep them on one line in a one-third card.
    const size = view.driveUrl ? "sm" : "md";
    const action = !folderHref
        ? null
        : view.action === "link_folder"
            ? <PillLink to={folderHref} variant="primary" icon={Link2Icon}>{"Link the client's folder"}</PillLink>
            : view.action === "open_folder"
                ? <PillLink to={folderHref} size={size} icon={FolderIcon}>Open the Client tab</PillLink>
                : null;
    const drive = view.driveUrl ? (
        <PillLink href={view.driveUrl} target="_blank" rel="noopener noreferrer" size={size} variant="primary" icon={ExternalLinkIcon}>
            Open in Drive
            <span className="sr-only">(opens Google Drive)</span>
        </PillLink>
    ) : null;
    return (
        // Positioned, so the screen-reader line stays inside the card on a phone.
        <div
            data-connector="google_docs"
            aria-busy={loading || undefined}
            className="relative flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-gray-300"
        >
            <div className="flex items-start gap-3">
                <ConnectorLogo k="google_docs" />
                <div className="min-w-0 flex-1">
                    <button
                        type="button"
                        aria-expanded={!folded}
                        onClick={() => setFolded((f) => !f)}
                        className="flex w-full min-w-0 items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                    >
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">{label}</span>
                        <ChevronDownIcon aria-hidden className={cn("h-4 w-4 shrink-0 text-gray-400 transition-transform", !folded && "rotate-180")} />
                    </button>
                    {folded ? (
                        view.tag && (
                            <span className="mt-1 block">
                                <DocsStatus tag={view.tag} />
                            </span>
                        )
                    ) : loading ? (
                        <>
                            <span aria-hidden className="mt-1.5 block h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                            <span className="sr-only">Checking the Google Docs connection</span>
                        </>
                    ) : (
                        <>
                            {view.lines.map((line, i) => (
                                <p
                                    key={i}
                                    role={view.alert && i === 0 ? "alert" : undefined}
                                    className="mt-0.5 break-words text-xs leading-relaxed text-gray-500"
                                >
                                    {line}
                                </p>
                            ))}
                            {writeLine && <p className="mt-0.5 break-words text-xs leading-relaxed text-gray-500">{writeLine}</p>}
                        </>
                    )}
                </div>
            </div>
            {!folded && (view.tag || action || drive) && (
                <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
                    {view.tag ? <DocsStatus tag={view.tag} /> : <span />}
                    {(action || drive) && (
                        <span className="flex flex-wrap items-center gap-2">
                            {drive}
                            {action}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Client documents: the Google Docs row for the client on screen.
 *
 * Its state comes from the Drive connection (route 1) and this client's
 * folder (route 3), not from the catalog's `status`, so a folder linked on
 * the Client tab shows here at once and a failed read never shows green. The
 * business id is the route's own: the screen's `businessId` prop is set only
 * for live businesses, and the fixture ones have folders too.
 */
function DocsSection({ entry, view, businessId, writeLine }: { entry: DataConnector; view: DocsConnectorView; businessId: string | null; writeLine: string | null }) {
    const folderHref = businessId ? clientFolderHref(businessId) : null;
    return (
        <CategorySection
            cat="docs"
            items={[entry]}
            summary={null}
            renderCard={(c) => <DocsConnectorCard key={c.key} label={c.label} view={view} folderHref={folderHref} writeLine={writeLine} />}
        />
    );
}

export function DataConnectors({
    connectors: catalog,
    businessId,
    gscState,
    onChanged,
}: {
    connectors: DataConnector[];
    /** Live business id. Absent on a fixture business, where connecting is not
     *  a real operation — the buttons stay inert there rather than pretending. */
    businessId?: string;
    /**
     * The reconciled Search Console state (see gscState.ts). Passed in rather
     * than derived here because the same state drives the Search Performance
     * screen and the Pages workshop, and three surfaces deciding it separately
     * is the bug this replaced.
     */
    gscState?: GscState;
    /** Re-fetch the catalog after a successful connect or disconnect. */
    onChanged?: () => void;
}) {
    const snackbar = useSnackbar();
    /** Everything below (cards, counts, the consent landing) reads this list. */
    const connectors = useMemo(() => catalog.filter(isOuraConnector), [catalog]);
    /**
     * The connectors someone connects: every card with a Connect button.
     * Google Docs is left out: it runs on ourteam's own account and has
     * nothing to connect or revoke. Its state is a separate read, and it
     * joins the header count once that read is known (`docsCounted`).
     */
    const connectable = useMemo(() => connectors.filter((c) => !STATUS_ONLY_CONNECTORS.has(c.key)), [connectors]);
    /** Drawn whether or not the catalog carries it: its state comes from the documents service. */
    const catalogDocs = connectors.find((c) => c.key === "google_docs") ?? null;
    const docsEntry = catalogDocs ?? GOOGLE_DOCS_CONNECTOR;
    // The Google Docs row's state, for its own card and for the header count.
    const { businessId: routeBusinessId } = useParams<{ businessId?: string }>();
    const docsBusinessId = routeBusinessId ?? null;
    const docsConnection = useDocsConnection();
    const docsFolder = useDocsFolder(docsBusinessId);
    const docsView = docsConnectorView(docsConnection, docsBusinessId ? docsFolder : null);
    // Phase 2 (B11), only with the Docs write switch: the write line beside the read lines.
    const docsWriteLine = DOCS_WRITE_ON && docsConnection.data ? docsWriteText(docsConnection.data.write) : null;
    // A backend that serves neither the catalog entry nor the connection
    // route has no documents yet: the row is absent, not "Not known".
    const docsShown = catalogDocs !== null || docsConnection.error?.reason !== "notServed";
    /**
     * The Google Docs row counts in the header once its state is known, so a
     * row reading "Connected" is in "n/total" like every other: 4 rows
     * connected read "4/5 connected". While its first reads are out, or when
     * they failed ("Not known"), it is in neither number: n/total has no
     * honest way to say "not known".
     */
    const docsCounted = docsShown && docsView.state !== "loading" && docsView.state !== "unknown";
    const connectedCount = connectable.filter((c) => c.status === "connected").length + (docsCounted && docsView.state === "connected" ? 1 : 0);
    /**
     * What "all of them" can actually mean.
     *
     * The denominator used to be every card on the tab, so a site on WordPress
     * with both Google accounts linked read "3/5 connected" and could never
     * read better than that: the card keeping it off 5 is Framer, which is an
     * alternative to the WordPress one rather than a second thing to connect.
     * An exclusive category can only ever contribute one, so that is what it
     * contributes here.
     *
     * Clamped up to `connectedCount` so the line can never read "3/2" if a
     * backend does report two platforms at once.
     *
     * The Google Docs row adds 1 once its state is known; see `docsCounted`.
     */
    const attainableTotal = useMemo(() => {
        const counted = new Set<ConnectorCategory>();
        const total = connectable.reduce((sum, c) => {
            if (!EXCLUSIVE_CATEGORIES.has(c.category)) return sum + 1;
            if (counted.has(c.category)) return sum;
            counted.add(c.category);
            return sum + 1;
        }, 0);
        return Math.max(total, connectable.filter((c) => c.status === "connected").length);
    }, [connectable]) + (docsCounted ? 1 : 0);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [refreshingKey, setRefreshingKey] = useState<string | null>(null);
    const [credentialKey, setCredentialKey] = useState<string | null>(null);
    /** Per-connector: what the last refresh said when it did not say
     *  "connected". Cleared by the next successful pull. */
    const [syncNotes, setSyncNotes] = useState<Record<string, string>>({});
    /** Set only when the browser refused the consent tab, so we can offer a
     *  link the user clicks themselves rather than losing their place. */
    const [pendingConsent, setPendingConsent] = useState<{ label: string; url: string } | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    /**
     * Land the user back from Google's consent screen.
     *
     * The OAuth callback is a BACKEND route, so the round trip ends outside the
     * SPA; the only way back in is the redirect the BE sends. Contract proposed
     * by the FE and pending on the BE side (see the handoff):
     *
     *   /oura/businesses/{id}/connectors?connected={key}&status=ok
     *   /oura/businesses/{id}/connectors?connected={key}&status=error&reason={text}
     *
     * Until the BE sends it, no param arrives and this is inert — the flow
     * degrades to exactly what it does today rather than breaking. `reason` is
     * BE-authored copy, so it renders as the snackbar's description and never
     * as markup.
     */
    useEffect(() => {
        const key = searchParams.get("connected");
        if (!key) return;
        const status = searchParams.get("status");
        const label = connectors.find((c) => c.key === key)?.label ?? "Account";
        if (status === "error") {
            snackbar.showError(
                `Could not connect ${label}`,
                searchParams.get("reason") || "Google did not finish the sign in. Please try again.",
            );
        } else {
            snackbar.showSuccess(
                `${label} connected`,
                "Oura reads this account on the next scan. Use Refresh to pull data now.",
            );
        }
        // Strip the params so a reload does not replay the confirmation, and
        // so the URL the user might copy is the plain connectors URL.
        const next = new URLSearchParams(searchParams);
        next.delete("connected");
        next.delete("status");
        next.delete("reason");
        setSearchParams(next, { replace: true });
        onChanged?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const startConnect = async (c: DataConnector) => {
        if (!businessId) {
            snackbar.showError("Not available here", "Connect a real business to link its accounts.");
            return;
        }
        // WordPress and Framer need credentials; Google needs a browser sign-in.
        if (CREDENTIAL_CONNECTORS.has(c.key)) {
            setCredentialKey(c.key);
            return;
        }
        // The tab has to be opened NOW, inside the click, or the browser treats
        // it as an unrequested popup and blocks it — by the time the connect
        // call resolves we are outside the user gesture. So: open a blank tab
        // synchronously, then point it at the consent URL when it arrives.
        //
        // `noopener` must NOT appear in the feature string. It is specified to
        // make window.open return null, so passing it cost us the handle this
        // whole dance exists to obtain: the tab still opened, we just could no
        // longer navigate it or close it, which stranded an about:blank and
        // sent the consent screen into THIS tab instead. Sever the child's
        // back-reference by hand — same protection, handle retained.
        const consentTab = window.open("", "_blank");
        if (consentTab) consentTab.opener = null;
        setBusyKey(c.key);
        const result = await connectConnector(businessId, c.key);
        setBusyKey(null);
        if (result.ok && result.kind === "redirect") {
            const safeUrl = getSafeConsentUrl(c.key, result.url);
            if (!safeUrl) {
                consentTab?.close();
                snackbar.showError(`Could not connect ${c.label}`, "The sign-in URL was not recognised.");
                return;
            }
            if (consentTab && !consentTab.closed) {
                consentTab.location.href = safeUrl;
                return;
            }
            // Popup blocked despite the synchronous open. Navigating this tab
            // would tear down the workspace the user is standing in, so hand
            // them a link instead: a click on a real anchor is a fresh user
            // gesture, which every blocker honours.
            setPendingConsent({ label: c.label, url: safeUrl });
            return;
        }
        // Anything other than a redirect means the blank tab is not going to be
        // used — close it rather than leaving an orphan about:blank behind.
        consentTab?.close();
        if (result.ok) {
            snackbar.showSuccess(`${c.label} connected`, result.detail);
            onChanged?.();
            return;
        }
        snackbar.showError(`Could not connect ${c.label}`, result.message);
    };

    /**
     * Re-pull one connector now, without the paid full re-scan.
     *
     * The 200 + `action_needed` shape is a pull that RAN and failed — the
     * BE's `detail` renders on the card (where the connector's state already
     * lives), never as an error toast. Either way the catalog and the business
     * detail are re-fetched via `onChanged`: the Clicks/Impressions/CTR/
     * Position numbers live on the business detail payload, not on the
     * connector, so a refresh that only re-read the catalog would leave the
     * dashes it was pressed to fix.
     */
    const startRefresh = async (c: DataConnector) => {
        if (!businessId) return;
        setBusyKey(c.key);
        setRefreshingKey(c.key);
        const result = await syncConnector(businessId, c.key);
        setBusyKey(null);
        setRefreshingKey(null);
        if (!result.ok) {
            snackbar.showError(`Could not refresh ${c.label}`, result.message);
            return;
        }
        if (result.connector.status === "connected") {
            setSyncNotes((prev) => {
                const next = { ...prev };
                delete next[c.key];
                return next;
            });
            snackbar.showSuccess(`${c.label} refreshed`, result.connector.detail);
        } else {
            // A 200 whose body we did not recognise lands here as
            // `action_needed` with an empty detail (see services/aeo). With the
            // note guarded on `detail` being non-empty, that case rendered
            // nothing at all and the button read as dead — the reported
            // "Refresh does nothing". Every outcome now leaves a trace.
            setSyncNotes((prev) => ({
                ...prev,
                [c.key]:
                    result.connector.detail ||
                    "The refresh ran, but the account sent nothing back. Check that the linked property covers this site.",
            }));
        }
        onChanged?.();
    };

    const startDisconnect = async (c: DataConnector) => {
        if (!businessId) return;
        setBusyKey(c.key);
        const ok = await disconnectConnector(businessId, c.key);
        setBusyKey(null);
        if (ok) {
            snackbar.showSuccess(`${c.label} disconnected`, "Oura will stop reading from this account.");
            onChanged?.();
        } else {
            snackbar.showError(`Could not disconnect ${c.label}`, "Please try again.");
        }
    };

    return (
        <div>
            <div className="mb-5 flex items-start justify-between gap-3">
                <p className="text-sm text-gray-500">
                    Connect the accounts Oura reads stats from and publishes fixes through. Tokens stay server side
                    and can be revoked at any time.
                </p>
                <span className="whitespace-nowrap text-xs text-gray-400 tabular-nums">
                    {connectedCount}/{attainableTotal} connected
                </span>
            </div>
            <div className="space-y-7">
                {CATEGORY_ORDER.map((cat) => {
                    if (cat === "docs") return docsShown ? <DocsSection key={cat} entry={docsEntry} view={docsView} businessId={docsBusinessId} writeLine={docsWriteLine} /> : null;
                    const items = connectable.filter((c) => c.category === cat);
                    if (items.length === 0) return null;
                    return (
                        <CategorySection
                            key={cat}
                            cat={cat}
                            items={items}
                            renderCard={(c) => (
                                <ConnectorCard
                                    key={c.key}
                                    c={c}
                                    busy={busyKey === c.key}
                                    refreshing={refreshingKey === c.key}
                                    health={c.key === "gsc" ? gscState : undefined}
                                    syncNote={syncNotes[c.key]}
                                    onConnect={() => void startConnect(c)}
                                    onDisconnect={() => void startDisconnect(c)}
                                    onRefresh={
                                        businessId && SYNCABLE_CONNECTORS.has(c.key)
                                            ? () => void startRefresh(c)
                                            : undefined
                                    }
                                />
                            )}
                        />
                    );
                })}
            </div>

            {pendingConsent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setPendingConsent(null)} />
                    <div className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-xl">
                        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                            <h2 className="text-base font-semibold text-gray-900">Allow the sign-in window</h2>
                            <button
                                onClick={() => setPendingConsent(null)}
                                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                aria-label="Close"
                            >
                                <XIcon className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="px-5 py-4">
                            <p className="my-0 text-sm leading-relaxed text-gray-500">
                                Your browser blocked the {pendingConsent.label} sign-in window. Open it yourself
                                and Oura picks the connection up when you come back. Your work here stays open.
                            </p>
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
                            <Button variant="ghost" onClick={() => setPendingConsent(null)}>
                                Cancel
                            </Button>
                            {/* An anchor, not a scripted open: the click is a fresh
                                user gesture, which is the one thing the blocker
                                that got us here will still honour. */}
                            <a
                                href={pendingConsent.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setPendingConsent(null)}
                                className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-blue-800 [&_svg]:h-4 [&_svg]:w-4"
                            >
                                <ExternalLinkIcon />
                                Continue to {pendingConsent.label}
                            </a>
                        </div>
                    </div>
                </div>
            )}

            <ConnectorConnectModal
                businessId={businessId ?? ""}
                connectorKey={credentialKey}
                onClose={() => setCredentialKey(null)}
                onConnected={(detail) => {
                    setCredentialKey(null);
                    snackbar.showSuccess("Connected", detail);
                    onChanged?.();
                }}
            />
        </div>
    );
}
